import config from '@config/index';
import katex from 'katex';

import { INLINE_MATH_REGEX } from '../doc-engine/parser/parser';

/** Decode HTML entities using a textarea element (works in browser + jsdom). */
const decodeHtmlEntities = (str: string): string => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = str;
  return textarea.value;
};

const replaceImages = (docId: string, token: string, html: string): string => {
  const regex = /data-blob-name=\"([^\"]*)\"/g;
  return html.replace(regex, (match, blobName) => {
    const decodedBlobName = decodeHtmlEntities(blobName);
    return `src="${config.AZURE_STORAGE_BASE_URL}/${docId}/${encodeURIComponent(decodedBlobName)}?${token}"`;
  });
};

const shouldSkipMathRendering = (textNode: Text): boolean => {
  let parent = textNode.parentElement;
  while (parent) {
    const tagName = parent.tagName.toLowerCase();
    if (tagName === 'code' || tagName === 'pre' || tagName === 'script' || tagName === 'style') return true;
    if (parent.classList.contains('katex')) return true;
    parent = parent.parentElement;
  }
  return false;
};

const renderFormulaToHtml = (formula: string): string => {
  try {
    return katex.renderToString(decodeHtmlEntities(formula), { throwOnError: false });
  } catch (error) {
    console.error('KaTeX rendering failed:', error);
    return `\\(${formula}\\)`;
  }
};

export const renderLatex = (html: string): string => {
  if (!html || !html.includes('\\(')) return html || '';

  const doc = new window.DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const candidates: Text[] = [];

  while (walker.nextNode()) {
    const textNode = walker.currentNode as Text;
    if (textNode.nodeValue?.includes('\\(') && !shouldSkipMathRendering(textNode)) {
      candidates.push(textNode);
    }
  }

  candidates.forEach((textNode) => {
    const text = textNode.nodeValue ?? '';
    if (!text.includes('\\(')) return;

    INLINE_MATH_REGEX.lastIndex = 0;
    const fragment = doc.createDocumentFragment();
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = INLINE_MATH_REGEX.exec(text))) {
      const [fullMatch, formula] = match;
      if (match.index > lastIndex) {
        fragment.append(doc.createTextNode(text.slice(lastIndex, match.index)));
      }

      const container = doc.createElement('span');
      container.innerHTML = renderFormulaToHtml(formula);
      while (container.firstChild) {
        fragment.append(container.firstChild);
      }

      lastIndex = match.index + fullMatch.length;
    }

    if (lastIndex < text.length) {
      fragment.append(doc.createTextNode(text.slice(lastIndex)));
    }

    textNode.parentNode?.replaceChild(fragment, textNode);
  });

  return doc.body.innerHTML || '';
};

export const applyImagePreprocessing = (docId: string, token: string, html: string): string =>
  replaceImages(docId, token, html);

export { replaceImages };
